import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Paperclip } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { TIERS } from "@/components/funding/TierBadge";
import { FundingReadyBadge } from "@/components/funding/FundingReadyBadge";
import { vaultCategory } from "@/components/funding/vaultCategory";
import { EXPIRING, ExpiryDialog } from "@/components/ExpiryDialog";

function Item({ r, check, onUpload }) {
  const file = useRef(null);
  const done = check.answers?.[r.id] === "yes" && check.proofs?.[r.id];
  return (
    <li className="flex items-start gap-2 text-sm py-1.5" data-testid={`vault-check-${r.id}`}>
      {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <Circle className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />}
      <div className="flex-1"><span className="text-slate-800">{r.proof || r.title}</span><span className="block text-xs text-slate-500">{r.title} · {r.issuer}</span></div>
      {!done && (
        <>
          <input ref={file} type="file" className="hidden" onChange={(e) => e.target.files[0] && onUpload(r, e.target.files[0])} data-testid={`vault-check-${r.id}-file`} />
          <button onClick={() => file.current.click()} className="text-xs text-emerald-700 font-medium inline-flex items-center gap-1 hover:underline shrink-0" data-testid={`vault-check-${r.id}-upload`}><Paperclip className="h-3 w-3" />Upload</button>
        </>
      )}
    </li>
  );
}

export function IndustryChecklist({ onUploaded }) {
  const [check, setCheck] = useState(undefined);
  const [expDoc, setExpDoc] = useState(null);
  const load = useCallback(() => api.get("/eligibility").then((r) => setCheck(r.data.find((c) => c.status === "ready") || null)), []);
  useEffect(() => { load(); }, [load]);
  if (check === undefined) return null;
  if (!check) return (
    <div className="bg-white border border-dashed border-slate-300 rounded-lg p-5 mb-6" data-testid="vault-checklist-empty">
      <h3 className="font-semibold text-slate-900">Your industry document checklist</h3>
      <p className="text-sm text-slate-600 mt-1">Run a free "Can I open this?" check to get the exact federal, state and local documents your business needs.</p>
      <Link to="/dashboard/funding" className="inline-block mt-3 text-sm font-semibold text-emerald-700 hover:underline" data-testid="vault-checklist-start-link">Start the check →</Link>
    </div>
  );
  const upload = async (r, f) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", vaultCategory(r));
    try {
      const d = await api.post("/documents", fd);
      await api.put(`/eligibility/${check.check_id}/answers`, { answers: { [r.id]: "yes" } });
      await api.put(`/eligibility/${check.check_id}/proof`, { req_id: r.id, doc_id: d.data.doc_id });
      toast.success("Saved and marked verified"); load(); onUploaded?.(); if (EXPIRING.has(d.data.category)) setExpDoc(d.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const total = check.requirements.length;
  const done = check.requirements.filter((r) => check.answers?.[r.id] === "yes" && check.proofs?.[r.id]).length;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="vault-checklist">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div><h3 className="font-semibold text-slate-900">Document checklist · {check.industry}, {check.state}</h3>
          <p className="text-xs text-slate-500" data-testid="vault-checklist-progress">{done} of {total} verified</p></div>
        {check.funding_ready ? <FundingReadyBadge ready testid="vault-compliant-badge" /> : <span className="text-xs text-slate-500">Upload every item to earn the 100% compliant badge</span>}
      </div>
      <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${total ? (100 * done) / total : 0}%` }} /></div>
      <div className="mt-4 grid md:grid-cols-2 gap-x-8">
        {["owner", "federal", "state", "local"].map((t) => {
          const items = check.requirements.filter((r) => r.tier === t);
          return items.length ? <div key={t} data-testid={`vault-checklist-${t}`}><h4 className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">{TIERS[t].label}</h4><ul>{items.map((r) => <Item key={r.id} r={r} check={check} onUpload={upload} />)}</ul></div> : null;
        })}
      </div>
      {expDoc && <ExpiryDialog doc={expDoc} onClose={() => setExpDoc(null)} onSaved={onUploaded} />}
    </div>
  );
}
